import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Image,
  Input,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import Paginator from '../../../components/paginator'
import { formatDateTime } from '../../../helpers'
import { IOurStudent } from '../../../interfaces/Testimonials/ITestimonials'
import { TestimonialsService } from '../../../services/Testimonials/TestimonialsService'
import { TestimonialsEditModal } from './TestimonialsEditModal'
import { TestimonialsFormModal } from './TestimonialsFormModal'

export default function OurStudentsPage() {
  const service = new TestimonialsService()
  const toast = useToast()
  const cancelRef = useRef(null)

  const [records, setRecords] = useState<IOurStudent[]>([])
  const [filters, setFilters] = useState({ page_size: '10' })
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const [search, setSearch] = useState('')
  const [filteredRecords, setFilteredRecords] = useState<IOurStudent[]>([])

  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [recordEditingId, setRecordEditingId] = useState<number | null>(null)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [recordDeletingId, setRecordDeletingId] = useState<number | null>(null)

  const [knowledgeAreas, setKnowledgeAreas] = useState<{ id: number; name: string }[]>([])

  const fetchStudents = async () => {
    try {
      const pageSize = parseInt(filters.page_size)
      if (isNaN(pageSize) || pageSize <= 0) return

      const response = await service.list({
        search: '',
        page,
        page_size: pageSize
      })

      setRecords(response.data)

      const pages = Math.ceil(response.recordsTotal / pageSize)
      setTotalPages(pages > 0 ? pages : 1)
    } catch (error) {
      toast({
        title: 'Erro ao buscar estudantes',
        description: 'Não foi possível carregar a lista de estudantes. Tente novamente mais tarde.',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }
  useEffect(() => {
    fetchStudents()
  }, [page, filters])

  useEffect(() => {
    service
      .listKnowledgeArea()
      .then(setKnowledgeAreas)
      .catch(() => {
        toast({
          title: 'Não foi possível carregar as áreas de conhecimento.',
          description: 'Por favor, tente novamente mais tarde.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right'
        })
      })
  }, [])

  useEffect(() => {
    setFilteredRecords(records.filter((student) => student.name.toLowerCase().includes(search.toLowerCase())))
  }, [search, records])

  function openEditModal(id: number) {
    setRecordEditingId(id)
    setIsEditModalOpen(true)
  }

  function closeEditModal() {
    setIsEditModalOpen(false)
    setRecordEditingId(null)
  }

  function closeFormModal() {
    setIsFormModalOpen(false)
  }

  function openDeleteDialog(id: number) {
    setRecordDeletingId(id)
    setIsDeleteOpen(true)
  }

  function closeDeleteDialog() {
    setIsDeleteOpen(false)
    setRecordDeletingId(null)
  }

  const handleDelete = async () => {
    if (!recordDeletingId) return

    try {
      await service.delete(recordDeletingId)

      toast({
        title: 'Estudante excluído com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom'
      })

      setRecords((prev) => prev.filter((student) => student.id !== recordDeletingId))
    } catch (error) {
      toast({
        title: 'Erro ao excluir estudante',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right'
      })
    } finally {
      closeDeleteDialog()
    }
  }

  return (
    <>
      <Heading as="h1" size="lg" mb="10" textTransform="uppercase">
        Depoimentos de alunos
      </Heading>

      <Flex mb="4" alignItems="center">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Input
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            type="number"
            min={1}
            onChange={(e) => {
              setFilters({ page_size: e.target.value })
              setPage(1)
            }}
            value={filters.page_size}
          />
          <FormLabel fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>

        <Spacer />

        <Flex flex="1" justifyContent="flex-end" alignItems="center">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome"
          />
        </Flex>
      </Flex>

      <TableContainer fontSize="smaller" padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th p={1}>ID</Th>
              <Th p={1}>Imagem</Th>
              <Th p={1}>Nome</Th>
              <Th p={1}>Área de Conhecimento</Th>
              <Th p={1}>Depoimento</Th>
              <Th p={1}>Criado em</Th>
              <Th p={1}>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredRecords.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center">
                  Nenhum estudante encontrado
                </Td>
              </Tr>
            ) : (
              filteredRecords
                .sort((a, b) => a.id - b.id)
                .map((student) => (
                  <Tr key={student.id}>
                    <Td p={1}>{student.id}</Td>
                    <Td p={1}>
                      <Box boxSize="38px" overflow="hidden" borderRadius="md">
                        <Image src={student.image_thumbnail} alt={student.name} objectFit="cover" boxSize="100%" />
                      </Box>
                    </Td>
                    <Td p={1}>{student.name}</Td>
                    <Td p={1}>{student.knowledge_area.name}</Td>
                    <Td p={1}>
                      <Tooltip label={student.description}>
                        <Text noOfLines={1} maxW="200px">
                          {student.description}
                        </Text>
                      </Tooltip>
                    </Td>

                    <Td p={1}>{student.created_at ? formatDateTime(student.created_at) : 'Não informado'}</Td>
                    <Td p={1}>
                      <Tooltip label="Editar" hasArrow placement="top">
                        <IconButton
                          aria-label="Editar"
                          colorScheme="blue"
                          size="sm"
                          mr={2}
                          onClick={() => openEditModal(student.id)}
                        >
                          <i className="lni lni-pencil"></i>
                        </IconButton>
                      </Tooltip>

                      <Tooltip label="Excluir" hasArrow placement="top">
                        <IconButton
                          aria-label="Excluir"
                          colorScheme="red"
                          size="sm"
                          mr={2}
                          onClick={() => openDeleteDialog(student.id)}
                        >
                          <i className="lni lni-trash-can"></i>
                        </IconButton>
                      </Tooltip>
                    </Td>
                  </Tr>
                ))
            )}
          </Tbody>
        </Table>

        <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={closeDeleteDialog} isCentered>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Confirmar exclusão
              </AlertDialogHeader>

              <AlertDialogBody>
                Tem certeza que deseja excluir o estudante com ID <strong>{recordDeletingId}</strong>?
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={closeDeleteDialog}>
                  Cancelar
                </Button>
                <Button colorScheme="red" onClick={handleDelete} ml={3}>
                  Excluir
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </TableContainer>

      <TestimonialsEditModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        id={recordEditingId ?? 0}
        knowledgeAreas={knowledgeAreas}
        onUpdated={fetchStudents}
      />

      <TestimonialsFormModal
        isOpen={isFormModalOpen}
        onClose={closeFormModal}
        knowledgeAreas={knowledgeAreas}
        onSuccess={() => {
          fetchStudents()
          setPage(1)
        }}
      />

      <Tooltip label="Adicionar manualmente" aria-label="Adicionar manualmente" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setIsFormModalOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      <Paginator page={page} totalPages={totalPages} changePage={(p) => setPage(p)} />
    </>
  )
}
